import { useEffect, useMemo, useRef } from "react";

/** Reveal each heading once per mount, only when it enters the viewport. */
export function MotionHeading({ text, as: Tag = "h2", className }: {
  text: string;
  as?: "h1" | "h2";
  className?: string;
}) {
  const ref = useRef<HTMLHeadingElement>(null);
  const played = useRef(false);
  const characters = useMemo(() => typeof Intl.Segmenter === "function"
    ? Array.from(new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(text), item => item.segment)
    : Array.from(text), [text]);

  useEffect(() => {
    const element = ref.current;
    if (!element || played.current) return;
    const motion = matchMedia("(prefers-reduced-motion: reduce)");
    if (motion.matches || !window.IntersectionObserver || !element.animate) {
      played.current = true;
      return;
    }
    const animations = new Set<Animation>();
    element.style.opacity = "0";
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.2)) return;
      played.current = true;
      element.dataset.revealed = "true";
      element.style.removeProperty("opacity");
      observer.disconnect();
      element.querySelectorAll<HTMLElement>("[data-character]").forEach((span, index) => {
        if (!span.textContent?.trim()) return;
        const animation = span.animate([
          { opacity: 0, filter: "blur(12px)", transform: "translateY(18px)" },
          { opacity: 1, filter: "blur(0)", transform: "translateY(0)" },
        ], { duration: 800, delay: index * 55, easing: "cubic-bezier(.22,1,.36,1)", iterations: 1, fill: "backwards" });
        animations.add(animation);
        animation.onfinish = () => { animations.delete(animation); animation.cancel(); };
      });
    }, { threshold: 0.2 });
    observer.observe(element);
    function reduceMotion() {
      if (!motion.matches) return;
      played.current = true;
      observer.disconnect();
      element?.style.removeProperty("opacity");
      animations.forEach(animation => animation.cancel());
    }
    motion.addEventListener("change", reduceMotion);
    return () => {
      observer.disconnect();
      motion.removeEventListener("change", reduceMotion);
      animations.forEach(animation => animation.cancel());
      element.style.removeProperty("opacity");
    };
  }, [text]);

  return <Tag ref={ref} className={className} aria-label={text}>{characters.map((char, index) => char === "\n"
    ? <br key={index} />
    : <span key={index} data-character aria-hidden="true" className="inline-block whitespace-pre">{char}</span>)}</Tag>;
}
